import { CordovaService } from '../cordova.service';
import { DataService, SubscribeForNotificationsType } from '../data/src';
import { PushAdapterErrorType, PushAdapterSettings } from '../models/notifications.model';
import { PushAdapterBase } from './push-adapter-base';

declare var PushNotification: any;

export class CordovaPushAdapter extends PushAdapterBase<string> {
  private push: any;
  constructor(
    coreDataService: DataService,
    cordovaService: CordovaService,
    channelIdentifier: string,
    settings: PushAdapterSettings,
    clientIdentifier: string
  ) {
    super(SubscribeForNotificationsType.GoogleFirebase, coreDataService, channelIdentifier, settings, clientIdentifier);

    if (cordovaService.isMobileDeviceReadyDetected) {
      super.notifyStateChanged(false, true);
    } else {
      cordovaService.onDeviceReady.subscribe(() => super.notifyStateChanged(false, true));
    }
  }

  private initBasics(unsubscribeBasicFuncsFirst: boolean = false): void {
    //asks for permissions if its the first time
    this.push = PushNotification.init({
      android: {},
      ios: {
        alert: 'true',
        badge: true,
        sound: 'false',
      },
    });

    if (unsubscribeBasicFuncsFirst) {
      this.push.off('error', this.onPushPluginError);
      this.push.off('notification', this.onPushPluginNotification);
    }

    this.push.on('error', this.onPushPluginError);
    this.push.on('notification', this.onPushPluginNotification);
  }

  private onPushPluginError = (err: any) => {
    this.notifyStateChanged(false, false);
    this.settings.onError(PushAdapterErrorType.ErrorInPushNotificationPlugIn, err);
  };

  private onPushPluginNotification = (data: any) => {
    data = this.convertPayloadToCommonFormat(data);
    if (this.settings.onNotificationReceived) {
      this.settings.onNotificationReceived(data);
    }
  };

  private onPushPluginRegistration = async (data: any) => {
    await this.notifyPushSubscription(data.registrationId);
  };

  private convertPayloadToCommonFormat(input: any): any {
    const output: any = {};
    if (input?.title !== undefined) {
      output.title = input.title;
    }
    if (input?.body !== undefined) {
      output.body = input.body;
    } else if (input?.message !== undefined) {
      output.body = input.message;
    }
    if (input?.additionalData && typeof input.additionalData === 'object') {
      Object.assign(output, input.additionalData);
    }
    return output;
  }

  protected override async enablePushNotificationsCore(): Promise<void> {
    try {
      this.initBasics();
      if (await this.isDenied()) {
        this.notifyStateChanged(false, false);
      } else {
        //ensure init after permissions granted
        this.initBasics(true);

        if (!this.existsMasterDBSubscription) {
          this.notifyStateChanged(true, false);
          this.push.on('registration', this.onPushPluginRegistration);
          this.push.subscribe(
            super.channelIdentifier,
            () => {},
            (err: any) => {
              this.notifyStateChanged(false, false);
              this.settings.onError(PushAdapterErrorType.UnableToEnablePushNotifications, err);
            }
          );
        } else {
          this.notifyStateChanged(true, true);
        }
      }
    } catch (err: any) {
      this.notifyStateChanged(false, false);
      this.settings.onError(PushAdapterErrorType.UnableToEnablePushNotifications, err);
    }
  }

  private async hasPermissions(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        PushNotification.hasPermission((data) => {
          resolve(data.isEnabled);
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  protected override async unsubscribe(): Promise<void> {
    await super.unsubscribe();
    this.push.unregister(
      () => {
        console.log('cordova: unsubscribe succeeded!');
      },
      () => {
        console.log('cordova: unsubscribe failed!');
      }
    );
    //unregister a specific topic
    // this.push.unsubscribe(
    //   this.TOPIC_opera,
    //   () => {
    //     console.log('unsubscribed from topic opera!');
    //   },
    //   (e: any) => {
    //     console.log('unsubscribe failed:', e);
    //   }
    // );
  }

  protected override async unsubscribeCore(subscription: string): Promise<void> {
    //foo
  }

  protected override async isGranted(): Promise<boolean> {
    return this.hasPermissions();
  }

  public override async isDenied(): Promise<boolean> {
    await this.delay(5000); //wait if permissions dialog is still open
    const hasPermission = await this.hasPermissions();
    return !hasPermission;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  protected async getProviderDefinition(subscription: string): Promise<object> {
    return { token: subscription };
  }
}
